// Library imports
import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { useTheme } from 'styled-components';
import { arrayOf, number, shape, string } from 'prop-types';
// Constant imports
import { customTheme } from './constants';

const PropBarChart = ({ data = [], xAxisLabel, yAxisLabel, yAxisKey, symbol = '', fixedAmount = 0, height = '25rem', propData = {} }) => {
    const theme = useTheme();

    if (!data.length || !yAxisKey) {
        return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No Data</div>;
    }

    const shouldRotate = data.length >= 10;

    const values = data.map(d => d[yAxisKey]);
    const minValue = Math.min(...values, 0);
    const maxValue = Math.max(1, ...values);

    // Check if we have playTimePerGame data
    const hasPlayTime = data.some(d => d.playTimePerGame !== undefined && d.playTimePerGame !== null);
    const playTimeValues = hasPlayTime ? data.map(d => d.playTimePerGame || 0) : [];
    const maxPlayTime = hasPlayTime ? Math.max(...playTimeValues) : 0;

    // Calculate scale factor to normalize playTime to the chart's Y-axis
    const scaleFactor = maxPlayTime > 0 ? maxValue / maxPlayTime : 1;

    const markers =
        propData?.connectedStat && propData.connectedStat === propData.id
            ? [
                  {
                      axis: 'y',
                      value: propData.value,
                      lineStyle: {
                          stroke: theme.colors?.secondary || '#ff4d4f',
                          strokeWidth: 3,
                          strokeDasharray: '5 5',
                      },
                      legend: `${propData.name}: ${propData.value}`,
                      legendPosition: 'top-left',
                      textStyle: {
                          fill: theme.colors?.text || '#333',
                          fontSize: 12,
                          fontWeight: 600,
                      },
                  },
              ]
            : [];

    const hasPropLine = markers.length > 0;
    const propValue = propData?.value;

    // Custom layer to draw playTimePerGame line
    const PlayTimeLayer = ({ bars, xScale, yScale }) => {
        if (!hasPlayTime) return null;

        const points = bars.map((bar, index) => {
            const playTime = data[index].playTimePerGame || 0;
            const scaledValue = playTime * scaleFactor;
            return {
                x: bar.x + bar.width / 2,
                y: yScale(scaledValue),
                value: playTime,
            };
        });

        const pathData = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');

        return (
            <g>
                {/* Line */}
                <path
                    d={pathData}
                    fill="none"
                    stroke="#f59e0b" // amber-500 for play time
                    strokeWidth={2}
                    strokeDasharray="4 4"
                />
                {/* Points */}
                {points.map((point, i) => (
                    <g key={i}>
                        <circle cx={point.x} cy={point.y} r={4} fill="#f59e0b" stroke="#fff" strokeWidth={2} />
                        <title>Play Time: {point.value.toFixed(1)} min</title>
                    </g>
                ))}
            </g>
        );
    };

    return (
        <div style={{ position: 'relative', height }}>
            {/* Custom Legend */}
            {hasPlayTime && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: '100px',
                        right: '0px',
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: 'rgba(255, 255, 255, 0.9)',
                        padding: '4px 8px',
                        borderRadius: '3px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        fontSize: '9px',
                        fontWeight: 500,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <svg width="12" height="2" style={{ display: 'block' }}>
                            <line x1="0" y1="1" x2="12" y2="1" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="2 2" />
                        </svg>
                        <span style={{ color: '#f59e0b' }}>Play Time</span>
                    </div>
                </div>
            )}
            <ResponsiveBar
                data={data}
                keys={[yAxisKey]}
                indexBy={xAxisLabel}
                minValue={0} // <-- enforce bottom of chart
                maxValue={maxValue} // <-- enforce top of chart
                layout="vertical"
                margin={{ top: 20, right: 20, bottom: 50, left: 0 }} // Adjust margins as needed
                padding={0.35}
                borderRadius={6}
                layers={['grid', 'axes', 'bars', 'markers', PlayTimeLayer, 'legends']} // Add custom layer
                colors={bar => {
                    if (hasPropLine) {
                        const barValue = bar.data[yAxisKey];
                        if (barValue > propValue) {
                            return '#22c55e'; // green-500 - above prop line
                        } else if (barValue < propValue) {
                            return '#ef4444'; // red-500 - below prop line
                        } else {
                            return '#3b82f6'; // blue-500 - equal to prop line
                        }
                    }
                    return '#3b82f6'; // default blue
                }}
                borderColor={{ from: 'color', modifiers: [['darker', 0.5]] }}
                borderWidth={2}
                theme={{
                    ...customTheme,
                    axis: {
                        ...customTheme.axis,
                        ticks: { line: { stroke: '#ccc', strokeWidth: 1 }, text: { fontSize: 12, fill: '#333' } },
                    },
                    grid: { line: { stroke: '#e0e0e0', strokeWidth: 1, strokeDasharray: '4 4' } },
                }}
                axisBottom={{
                    renderTick: tick => {
                        const [opponent, date] = tick.value.split('|');

                        return (
                            <g transform={`translate(${tick.x},${tick.y + 10}) rotate(${shouldRotate ? -15 : 0})`}>
                                <text
                                    textAnchor={shouldRotate ? 'end' : 'middle'}
                                    dominantBaseline="hanging"
                                    style={{ fontSize: shouldRotate ? 10 : 12, fontWeight: 600, fill: '#555' }}
                                >
                                    {opponent}
                                </text>
                                <text
                                    textAnchor={shouldRotate ? 'end' : 'middle'}
                                    dominantBaseline="hanging"
                                    dy={shouldRotate ? 12 : 14}
                                    style={{ fontSize: 10, fill: '#999' }}
                                >
                                    {date}
                                </text>
                            </g>
                        );
                    },
                }}
                axisLeft={{
                    tickSize: 0, // Keep the axis clean
                    tickPadding: 10,
                    tickRotation: 0,
                    legend: yAxisLabel,
                    legendOffset: -50,
                    legendPosition: 'middle',
                    domain: [minValue, maxValue], // NOTE does not control the actual y-axis scale. It only affects the ticks displayed on the axis
                    format: () => '', // Hide all tick labels
                }}
                tooltip={({ id, value, indexValue, color, data }) => {
                    const [opponent, date] = indexValue.split('|');
                    const playTime = data.playTimePerGame;
                    return (
                        <div
                            style={{
                                background: '#fff',
                                padding: '8px 12px',
                                border: `1px solid ${color}`,
                                borderRadius: 4,
                                boxShadow: '0px 2px 6px rgba(0,0,0,0.1)',
                                fontSize: 12,
                            }}
                        >
                            <div>
                                <strong>Opponent:</strong> {opponent}
                            </div>
                            <div>
                                <strong>Date:</strong> {date}
                            </div>
                            <div>
                                <strong>{id}:</strong> {value.toFixed(fixedAmount)}
                                {symbol}
                            </div>
                            {playTime !== undefined && playTime !== null && (
                                <div>
                                    <strong style={{ color: '#f59e0b' }}>Time on Ice:</strong>{' '}
                                    <span style={{ color: '#f59e0b' }}>{playTime.toFixed(1)} min</span>
                                </div>
                            )}
                        </div>
                    );
                }}
                label={d => (
                    <tspan
                        y={d.value > 0 ? 10 : -10}
                        style={{
                            fill: '#fff',
                            fontSize: 12,
                            fontWeight: 600,
                            ...(d.value === 0 && {
                                stroke: '#000',
                                strokeWidth: 2,
                                paintOrder: 'stroke',
                            }),
                        }}
                    >
                        {d.value.toFixed(fixedAmount)}
                        {symbol}
                    </tspan>
                )}
                labelSkipHeight={0}
                labelTextColor={{ from: 'color', modifiers: [['darker', 1.8]] }}
                markers={markers}
                animate
                motionStiffness={90}
                motionDamping={15}
            />
        </div>
    );
};

PropBarChart.propTypes = {
    data: arrayOf(shape({})),
    xAxisLabel: string.isRequired,
    yAxisLabel: string.isRequired,
    yAxisKey: string.isRequired,
    symbol: string,
    fixedAmount: number,
    height: string,
    propData: shape({
        id: string,
        name: string,
        value: number,
        connectedStat: string,
    }),
};

export default PropBarChart;
